import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';

import {
  getMintConfirmApi,
  getMintExchangeRateApi,
  getMintOverviewApi,
  getMintPendingApi,
  postMintPreviewApi,
} from '@flipster/apis/v1/earn';
import { MintSide } from '@flipster/apis/v1/earn/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { v1CurrenciesBunja } from '@flipster/features/src/shared/currency/use-v1-currencies';
import { i18nBunja } from '@flipster/i18n';
import { overlayBunja } from '@flipster/overlay';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { DefaultNumberInputMaxLength } from '@flipster/ui';
import { checkAxiosError } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode } from '@flipster/utils/error';
import Num, { inputFormatter, numberFormatter } from '@flipster/utils/num';

import MintFlowModal from './index';

export const mintBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const overlay = bunja.use(overlayBunja);
  const { walletsAtom } = bunja.use(walletsBunja);
  const { currenciesKeyByCurrencyAtom } = bunja.use(v1CurrenciesBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  // Minting 하려는 currency - USDE
  const providerCurrencyAtom = atom('');
  // Minting 하기 위해서 담보로 입금하는 currency - USDT/USDC
  const collateralCurrencyAtom = atom('');

  const flowTypeAtom = atom<MintSide>('MINT');
  const availableBalanceAtom = atom((get) => {
    const mintPendingRequest = get(getMintPendingAtom);
    if (mintPendingRequest.isLoading) return '0';
    const pendingMints = mintPendingRequest.data?.data.pendingRequests ?? [];
    const providerCurrency = get(providerCurrencyAtom);
    const collateralCurrency = get(collateralCurrencyAtom);
    const flowType = get(flowTypeAtom);
    const currency = flowType === 'MINT' ? collateralCurrency : providerCurrency;

    const wallets = get(walletsAtom);
    const currenciesKeyByCurrency = get(currenciesKeyByCurrencyAtom);
    const currencyInfo = currenciesKeyByCurrency?.[currency];
    const precision = currencyInfo?.fullDisplayPrecision;

    // Pending Mint/Redeem인 금액 제외
    const pendingBalance = pendingMints.reduce((acc, pendingMint) => {
      if (pendingMint.side === flowType && pendingMint.providerCurrency === providerCurrency) {
        if (flowType === 'MINT' && pendingMint.collateralCurrency === collateralCurrency) {
          return acc.plus(pendingMint.fromAmount);
        } else if (flowType === 'REDEEM') {
          // REDEEM의 경우 collateralCurrency와 상관없이 providerCurrency의 금액에서 빼야 함
          // REDEEM도 fromAmount인 이유는, flowType(side)만 다를 뿐 Mint flow에서도 fromAmount를 사용하기 때문임
          return acc.plus(pendingMint.fromAmount);
        }
      }
      return acc;
    }, Num('0'));

    const amount = Num(wallets?.[currency]?.walletBalanceExclLocked ?? '0')
      .minus(pendingBalance)
      .toFixed(precision ?? 8);

    return Num(amount).lte(0) ? '0' : amount;
  });

  const getMintOverviewAtom = atomWithQuery((get) => {
    const currency = get(providerCurrencyAtom);
    return {
      queryKey: ['getMintOverviewApi', currency],
      queryFn: () => getMintOverviewApi({ currency }),
      enabled: !!currency,
    };
  });

  const getMintExchangeRateAtom = atomWithQuery((get) => {
    const flowType = get(flowTypeAtom);
    const collateralCurrency = get(collateralCurrencyAtom);
    const providerCurrency = get(providerCurrencyAtom);
    return {
      queryKey: ['mintExchangeRate', collateralCurrency, providerCurrency],
      queryFn: () => getMintExchangeRateApi({ collateralCurrency, providerCurrency, side: flowType }),
      enabled: !!collateralCurrency && !!providerCurrency,
      retry: 10,
    };
  });

  const getMintPendingAtom = atomWithQuery((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: ['getMintPending'],
      queryFn: getMintPendingApi,
      refetchOnMount: 'always',
      enabled: isCookieLoggedIn,
    };
  });

  const mintOverviewAtom = atom((get) => get(getMintOverviewAtom).data?.data);
  const mintExchangeRateAtom = atom((get) => get(getMintExchangeRateAtom).data?.data);
  const collateralCurrenciesAtom = atom((get) => get(mintOverviewAtom)?.collateralCurrencies ?? []);

  // Sort collateral currencies by wallet balance (convertibleBalance) in descending order
  // The first item will be the default selection (max balance)
  const sortedCollateralCurrenciesAtom = atom((get) => {
    const collateralCurrencies = get(collateralCurrenciesAtom);
    const wallets = get(walletsAtom);

    return [...collateralCurrencies].sort((a, b) => {
      const balanceA = Num(wallets?.[a.currency]?.walletBalanceExclLocked ?? '0');
      const balanceB = Num(wallets?.[b.currency]?.walletBalanceExclLocked ?? '0');
      return balanceB.minus(balanceA).toNumber();
    });
  });

  const isLoadingAtom = atom((get) => {
    const getMintOverview = get(getMintOverviewAtom);
    const getMintExchangeRate = get(getMintExchangeRateAtom);
    return getMintOverview.isLoading || getMintExchangeRate.isLoading;
  });

  // Collateral currency의 잔액들이 전부 minAmount보다 작은 경우에 Deposit guide를 보여줘야 함
  const needToDepositMoreAtom = atom((get) => {
    const flowType = get(flowTypeAtom);

    if (flowType === 'REDEEM') return false;

    const mintOverview = get(mintOverviewAtom);
    const wallets = get(walletsAtom);

    return mintOverview?.collateralCurrencies.every(({ currency, minAmount }) => {
      const wallet = wallets?.[currency]?.walletBalanceExclLocked ?? '0';
      return Num(wallet).lt(minAmount);
    });
  });

  function openMintModal({ providerCurrency, flowType = 'MINT' }: { providerCurrency: string; flowType?: MintSide }) {
    store.set(flowTypeAtom, flowType);
    store.set(providerCurrencyAtom, providerCurrency);
    store.set(collateralCurrencyAtom, '');

    overlay.open(({ isOpen, close, unmount }) => <MintFlowModal isOpen={isOpen} onClose={close} onExit={unmount} />, {
      slot: 'mintFlowModal',
    });
  }

  return {
    openMintModal,
    mintOverviewAtom,
    mintExchangeRateAtom,
    collateralCurrenciesAtom,
    sortedCollateralCurrenciesAtom,
    providerCurrencyAtom,
    collateralCurrencyAtom,
    availableBalanceAtom,
    flowTypeAtom,
    isLoadingAtom,
    needToDepositMoreAtom,
  };
});
mintBunja.debugLabel = 'mintBunja';

export const mintFormBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const {
    collateralCurrenciesAtom,
    availableBalanceAtom,
    flowTypeAtom,
    mintExchangeRateAtom,
    providerCurrencyAtom,
    collateralCurrencyAtom,
  } = bunja.use(mintBunja);
  const { getT } = bunja.use(i18nBunja);
  const t = getT();

  // Form에서는 MINT | REDEEM에 따라 from-to가 바뀌므로 구분하기 쉽게 분리함
  const fromCurrencyAtom = atom(
    (get) => {
      const flowType = get(flowTypeAtom);
      return flowType === 'MINT' ? get(collateralCurrencyAtom) : get(providerCurrencyAtom);
    },
    (get, set, newCurrency: string) => {
      const flowType = get(flowTypeAtom);
      if (flowType === 'MINT') {
        set(collateralCurrencyAtom, newCurrency);
      } else {
        set(providerCurrencyAtom, newCurrency);
      }
    },
  );

  const toCurrencyAtom = atom(
    (get) => {
      const flowType = get(flowTypeAtom);
      return flowType === 'MINT' ? get(providerCurrencyAtom) : get(collateralCurrencyAtom);
    },
    (get, set, newCurrency: string) => {
      const flowType = get(flowTypeAtom);
      if (flowType === 'MINT') {
        set(providerCurrencyAtom, newCurrency);
      } else {
        set(collateralCurrencyAtom, newCurrency);
      }
    },
  );

  const fromAmountAtom = atom('');
  const toAmountAtom = atom((get) => {
    const fromAmount = get(fromAmountAtom);
    const mintExchangeRate = get(mintExchangeRateAtom)?.rate;
    if (!fromAmount || !mintExchangeRate) return '';
    const value = Num(fromAmount).multipliedBy(mintExchangeRate).toFixed(8).toString();
    return inputFormatter(value, {
      withComma: true,
      maxLength: DefaultNumberInputMaxLength,
    }).replace(/,/g, '');
  });

  const collateralCurrencyInfoAtom = atom((get) => {
    const flowType = get(flowTypeAtom);
    const fromCurrency = get(fromCurrencyAtom);
    const toCurrency = get(toCurrencyAtom);
    const availableCurrencies = get(collateralCurrenciesAtom);
    return availableCurrencies.find(({ currency }) =>
      flowType === 'MINT' ? currency === fromCurrency : currency === toCurrency,
    );
  });

  const minAmountAtom = atom((get) => {
    const collateralCurrencyInfo = get(collateralCurrencyInfoAtom);
    return collateralCurrencyInfo?.minAmount ?? '0';
  });

  const errorMessageAtom = atom((get) => {
    const flowType = get(flowTypeAtom);
    const fromAmount = get(fromAmountAtom);
    const fromCurrency = get(fromCurrencyAtom);
    const availableBalance = get(availableBalanceAtom);
    const collateralCurrencyInfo = get(collateralCurrencyInfoAtom);
    const minAmount = get(minAmountAtom);

    const maxAmount = collateralCurrencyInfo?.maxAmount;

    if (!fromAmount || !fromCurrency) {
      return null;
    }

    const amount = Num(fromAmount);
    const balance = Num(availableBalance);

    if (minAmount && amount.lt(minAmount)) {
      return t(
        flowType === 'MINT'
          ? 'Mint.r251008.You_need_to_mint_at_least_size_error'
          : 'Redeem.r251008.You_need_to_redeem_at_least_amount_quote_currency',
        {
          amountQuoteCurrency: `${numberFormatter(minAmount, { withComma: true, toFixed: !Num(minAmount).isInteger() })} ${fromCurrency}`,
        },
      );
    }

    if (maxAmount && (amount.gt(maxAmount) || amount.gt(balance))) {
      const limit = Num(maxAmount).lt(balance) ? maxAmount : availableBalance;
      return t(
        flowType === 'MINT'
          ? 'Mint.r251008.You_can_mint_only_up_to_size_error'
          : 'Redeem.r251008.You_can_redeem_only_up_to_amount_quote_currency',
        {
          amountQuoteCurrency: `${numberFormatter(limit, { withComma: true, toFixed: !Num(limit).isInteger() })} ${fromCurrency}`,
        },
      );
    }

    return null;
  });

  bunja.effect(() => {
    return store.sub(fromCurrencyAtom, () => {
      store.set(fromAmountAtom, '');
    });
  });

  return {
    fromCurrencyAtom,
    toCurrencyAtom,
    fromAmountAtom,
    toAmountAtom,
    minAmountAtom,
    errorMessageAtom,
  };
});
mintFormBunja.debugLabel = 'mintFormBunja';

export const mintPreviewBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { getMintServerErrorMessage } = bunja.use(mintServerErrorBunja);
  const { getT } = bunja.use(i18nBunja);
  const t = getT();
  const { collateralCurrencyAtom, providerCurrencyAtom, flowTypeAtom, mintOverviewAtom } = bunja.use(mintBunja);
  const { fromAmountAtom, fromCurrencyAtom, toCurrencyAtom } = bunja.use(mintFormBunja);

  const postMintPreviewAtom = atomWithMutation((get) => {
    const collateralCurrency = get(collateralCurrencyAtom);
    const providerCurrency = get(providerCurrencyAtom);
    const fromAmount = get(fromAmountAtom);
    const flowType = get(flowTypeAtom);

    return {
      mutationKey: ['postMintPreviewApi', collateralCurrency, providerCurrency, fromAmount, flowType],
      mutationFn: () => postMintPreviewApi({ collateralCurrency, providerCurrency, fromAmount, side: flowType }),
      onError: (error) => {
        if (!checkAxiosError(error)) return;
        const errorCode = getErrorCode(error);
        const errorMessage = getMintServerErrorMessage(errorCode);
        snackbar.error({ message: errorMessage });
      },
    };
  });

  const isPreviewLoadingAtom = atom((get) => get(postMintPreviewAtom).isPending);
  const previewIdAtom = atom<number>();
  const fromAmountPreviewAtom = atom('');
  const toAmountPreviewAtom = atom('');
  const estimateTimeAtom = atom((get) => get(mintOverviewAtom)?.estProcessingTimeInMins);

  const postMintConfirmAtom = atomWithMutation((get) => {
    const id = get(previewIdAtom);

    return {
      mutationKey: ['getMintConfirmApi', id],
      mutationFn: () => getMintConfirmApi({ id: id! }),
      onError: (error) => {
        if (!checkAxiosError(error)) return;
        const errorCode = getErrorCode(error);
        const errorMessage = getMintServerErrorMessage(errorCode);
        snackbar.error({ message: errorMessage });
      },
      onSuccess: () => {
        snackbar.success({ message: t('Common.r251008.Your_request_is_submitted') });
      },
    };
  });

  async function postMintPreview({ onSuccess }: { onSuccess: () => void }) {
    const { data } = await store.get(postMintPreviewAtom).mutateAsync();

    if (data) {
      store.set(previewIdAtom, data.id);
      if (data.side === 'MINT') {
        store.set(fromAmountPreviewAtom, data.collateralAmount);
        store.set(toAmountPreviewAtom, data.providerAmount);
      } else {
        store.set(fromAmountPreviewAtom, data.providerAmount);
        store.set(toAmountPreviewAtom, data.collateralAmount);
      }

      onSuccess();
    }
  }

  return {
    postMintPreview,
    postMintConfirmAtom,
    isPreviewLoadingAtom,
    fromCurrencyAtom,
    toCurrencyAtom,
    fromAmountAtom: fromAmountPreviewAtom,
    toAmountAtom: toAmountPreviewAtom,
    flowTypeAtom,
    estimateTimeAtom,
  };
});
mintPreviewBunja.debugLabel = 'mintPreviewBunja';

export const mintServerErrorBunja = bunja(() => {
  const { getT } = bunja.use(i18nBunja);
  const t = getT();

  const getMintServerErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      // TODO: [sh] 디자인이나 번역이 없음 - 우선 에러메세지 정리하고 후속 작업
      // case 'api.mint.overview.invalidCurrency':
      // case 'api.mint.exchangeRate.invalidCurrency':
      // case 'api.mint.preview.invalidCurrency':
      // case 'api.mint.preview.invalidAmount':
      // TODO: [sh] 이건 번역은 있는데 에러코드가 명확하지 않음
      // return t('Convert.r250401.The_conversion_has_been_canceled_because_the_process_timed_out');
      default:
        return t('Convert.r250401.There_was_an_unexpected_server_error_please_try_again_later');
    }
  };

  return {
    getMintServerErrorMessage,
  };
});
mintServerErrorBunja.debugLabel = 'mintServerErrorBunja';
